// 軍配者たち 3部屋目の装置の現れ方・比較版 C「上から落ちる重さ」(2026-10-31・開発版 ?twinsReveal=c)。
//   作者 (10-30)「イメージは合ってるけどもっと邪悪かつ強そうにしてほしい」→ イメージ画3案から C を選んだ (10-30 夜)。
//   流れ (ms は REVEAL_DROP): 部屋が暗くなる → 倒れた二人から光が浮かぶ → 光が装置の真上の一点へ吸い込まれ、そのまわりの空気がゆがむ
//   → 光が呑まれて黒い点だけが残り、床に装置の影が広がる → 装置が真上から落ちて床を割る → 灰と砂ぼこりが左右へ吹き荒れ、暗さが引く。
//   色は黒・灰・冷たい青・白だけ (朱は装置の紐だけ・赤い光と紫の炎は使わない)。
//   線や輪は引かない: 光はぼかしの塊、ひび・土ぼこり・灰は介錯の描いた絵 (public/fx/exec/mats/) に色を付けて置く。
import { REVEAL_DROP as T } from './twins_fight'
import { crackBitSprite, crackSprite, drawMat, dustSprite, mistSprite, tinted } from './exec_fx_mats'

type Rect = { x0: number; x1: number; top: number; bottom: number }
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
function hash(n: number): number { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x) }

/** 冷たい青白のぼかしの塊 (加算で重ねる)。 */
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number): void {
  if (a <= 0.003 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(236,246,255,${a})`)
  g.addColorStop(0.35, `rgba(170,205,255,${a * 0.5})`)
  g.addColorStop(1, 'rgba(120,160,230,0)')
  ctx.fillStyle = g
  ctx.fillRect(x - r, y - r, r * 2, r * 2)
}

/** 光が吸い込まれる一点 (装置の天辺のすこし上)。 */
export function dropPoint(r: Rect): { x: number; y: number } {
  return { x: (r.x0 + r.x1) / 2, y: r.top - 40 }
}

/** 部屋の暗さ (0..1)。吸い込みで深まり、床が割れた後に引く。 */
export function dropDarkness(e: number): number {
  if (e < T.dark) return 0
  if (e < T.swallow) return 0.5 * clamp01((e - T.dark) / (T.swallow - T.dark))
  if (e < T.appear) return 0.5 + 0.12 * clamp01((e - T.swallow) / 200)
  return 0.62 * (1 - clamp01((e - T.appear) / (T.end - T.appear))) ** 1.5
}

/** 落ちてくる装置の縦のずれ (px・0 = 床に着いた)。落ちる前は null (まだ見せない)。 */
export function dropOffset(e: number, r: Rect): number | null {
  if (e < T.fall) return null
  const u = clamp01((e - T.fall) / (T.appear - T.fall))
  return -(r.bottom + 20) * (1 - u * u)
}

/** 粒の位置。倒れた体から浮かび、上の一点へ渦を巻いて吸い込まれる。 */
export function dropMote(e: number, bodyX: number, k: number, p: { x: number; y: number }, floor: number): { x: number; y: number; a: number; r: number } | null {
  const t0 = T.rise + hash(k) * 420
  if (e < t0 || e >= T.swallow) return null
  const dx = (hash(k + 0.5) - 0.5) * 90, sy = floor - 20 - hash(k + 0.25) * 60, riseH = 50 + hash(k + 0.75) * 90
  const u = clamp01((e - t0) / (T.gather - T.rise))
  const ease = 1 - (1 - u) * (1 - u)
  const rx = bodyX + dx + Math.sin(e / 240 + k * 1.7) * 8 * ease, ry = sy - riseH * ease
  const v = clamp01((e - T.gather + 200 + hash(k + 0.3) * 200) / (T.swallow - T.gather))
  const pull = v * v * v
  // 吸い込まれる時は一点のまわりを回りながら縮む。
  const ang = pull * (2.2 + hash(k + 0.9) * 1.5) * (k % 2 ? 1 : -1)
  const ox = (rx - p.x) * (1 - pull), oy = (ry - p.y) * (1 - pull)
  const x = p.x + ox * Math.cos(ang) - oy * Math.sin(ang), y = p.y + ox * Math.sin(ang) + oy * Math.cos(ang)
  return { x, y, a: Math.min(1, (e - t0) / 220) * (0.6 + 0.4 * pull), r: (8 + hash(k + 0.1) * 6) * (1 - 0.6 * pull) }
}

/** 一点のまわりの空気のゆがみ: 画面の横の細い帯をずらして描き直す (線は引かない)。 */
function warp(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, e: number, amount: number): void {
  if (amount <= 0.01 || typeof ctx.getTransform !== 'function') return
  const m = ctx.getTransform(), cv = ctx.canvas
  const px = m.a * p.x + m.e, py = m.d * p.y + m.f, R = 190 * m.a, band = Math.max(2, Math.round(3 * m.d))
  ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0)
  for (let y = Math.max(0, py - R); y < Math.min(cv.height, py + R); y += band) {
    const dy = (y - py) / R, half = R * Math.sqrt(Math.max(0, 1 - dy * dy))
    if (half < 2) continue
    const fall = 1 - Math.abs(dy)
    const off = Math.sin(y * 0.09 / m.d - e * 0.018) * 7 * m.a * amount * fall * fall
    const x0 = Math.max(0, px - half), w = Math.min(cv.width, px + half) - x0
    if (w > 1) ctx.drawImage(cv, x0, y, w, band, x0 + off, y, w, band)
  }
  ctx.restore()
}

/** 床が割れた所 (装置の足もと) の x。 */
function feet(r: Rect): number[] {
  const w = r.x1 - r.x0
  return [r.x0 + w * 0.27, r.x0 + w * 0.73]
}

/** 装置の後ろ (床・暗さ・吸い込み・落下・ひび)。drawDevice より先に呼ぶ。落ちている装置もここで描く。 */
export function drawDropBehind(
  ctx: CanvasRenderingContext2D, e: number, r: Rect, floor: number,
  bodies: { x: number }[], drawBodies: (alpha: number) => void,
  drawDeviceAt: (dy: number, stretch: number) => void,
): void {
  // 部屋を暗くする (背景だけ・装置と人は上に描く)。
  const dark = dropDarkness(e)
  if (dark > 0.005) { ctx.fillStyle = `rgba(3,5,11,${dark})`; ctx.fillRect(r.x0 - 3000, -3000, 7000, 7000) }
  // 割れた床は残す (3部屋目のあいだずっと)。
  if (e >= T.appear) drawCracks(ctx, e - T.appear, r)
  if (e >= T.end) return
  const p = dropPoint(r)
  // 倒れた二人は光が浮かぶにつれて薄れる。
  const fade = 1 - clamp01((e - T.rise) / (T.gather - T.rise))
  if (fade > 0 && e < T.appear) drawBodies(fade)
  // 空気のゆがみ: 吸い込みの終わりに強まり、光が呑まれてから落ちるまで残る。
  if (e >= T.gather - 300 && e < T.appear) {
    const a = e < T.swallow ? clamp01((e - T.gather + 300) / (T.swallow - T.gather + 300)) : 1 - 0.5 * clamp01((e - T.swallow) / (T.appear - T.swallow))
    warp(ctx, p, e, a)
  }
  // 床に広がる装置の影 (落ちてくる前の予兆)。
  if (e >= T.swallow - 150 && e < T.appear + 60) {
    const u = clamp01((e - T.swallow + 150) / (T.appear - T.swallow + 150))
    const cx = (r.x0 + r.x1) / 2, w = (r.x1 - r.x0) * (0.35 + 0.6 * u * u)
    const g = ctx.createRadialGradient(cx, 0, 0, cx, 0, w / 2)
    g.addColorStop(0, `rgba(0,0,0,${0.15 + 0.55 * u})`); g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.save(); ctx.translate(0, r.bottom + 2); ctx.scale(1, 0.09); ctx.fillStyle = g
    ctx.fillRect(cx - w / 2, -w / 2, w, w); ctx.restore()
  }
  if (e < T.swallow + 300) {
    ctx.save(); ctx.globalCompositeOperation = 'lighter'
    bodies.forEach((b, bi) => {
      glow(ctx, b.x, floor - 50, 80, 0.2 * clamp01((e - T.rise) / 400) * fade)
      for (let i = 0; i < 16; i++) {
        const m = dropMote(e, b.x, bi * 100 + i, p, floor)
        if (m) glow(ctx, m.x, m.y, m.r * 2.4, m.a * 0.8)
      }
    })
    // 一点の光: 集まって膨らみ、吸い込みの終わりに小さく固く縮み、呑まれて消える。
    let rad = 0, a = 0
    if (e >= T.gather - 200 && e < T.swallow) {
      const v = clamp01((e - T.gather + 200) / (T.swallow - T.gather + 200))
      rad = v < 0.6 ? 30 + 80 * (v / 0.6) : 110 - 95 * ((v - 0.6) / 0.4) ** 0.7
      a = 0.3 + 0.7 * v
    } else if (e >= T.swallow) {
      const v = clamp01((e - T.swallow) / 300)
      rad = 15 * (1 - v); a = 1 - v
    }
    glow(ctx, p.x, p.y, rad, a)
    ctx.restore()
  }
  // 呑まれた後の黒い点 (光を吸った穴)。縁にだけ冷たい青がにじむ。
  //   縁は輪に見えないよう、黒からぼかしで溶かし、冷たい青は広く薄くだけ。装置が落ち始めると一気に閉じる。
  if (e >= T.swallow - 60 && e < T.fall + 80) {
    const v = clamp01((e - T.swallow + 60) / 160), out = clamp01((e - T.fall) / 80)
    const rad = (16 + 3 * Math.sin(e / 40) + 14 * v) * (1 - out * out)
    if (rad > 1) {
      const halo = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad * 4)
      halo.addColorStop(0, `rgba(80,115,185,${0.16 * v})`); halo.addColorStop(1, 'rgba(60,90,160,0)')
      ctx.save(); ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = halo
      ctx.fillRect(p.x - rad * 4, p.y - rad * 4, rad * 8, rad * 8); ctx.restore()
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rad * 2)
      g.addColorStop(0, `rgba(0,0,0,${0.95 * v})`)
      g.addColorStop(0.35, `rgba(1,2,6,${0.85 * v})`)
      g.addColorStop(1, 'rgba(3,5,11,0)')
      ctx.fillStyle = g; ctx.fillRect(p.x - rad * 2, p.y - rad * 2, rad * 4, rad * 4)
    }
  }
  // 落ちてくる装置 (床に着くまで)。速いほど縦にわずかに伸びる。
  const dy = dropOffset(e, r)
  if (dy !== null && e < T.appear) {
    const u = clamp01((e - T.fall) / (T.appear - T.fall))
    drawDeviceAt(dy, 1 + 0.07 * u * u)
  }
}

/** 割れた床。足もとから左右へ伸び、残る (色を付けた絵・赤い光は使わない)。 */
function drawCracks(ctx: CanvasRenderingContext2D, since: number, r: Rect): void {
  const stage = Math.min(3, Math.floor(since / 45))
  const img = crackSprite(stage)
  if (!img) return
  // 装置の足は床の奥の縁に立つので、ひびは足もとから手前の床板へ広がる高さに置く。
  const y = r.bottom + 12
  const dark = tinted(img, '6,6,9'), cold = tinted(img, '150,190,255')
  const seam = since < 1100 ? 0.5 * (1 - since / 1100) : 0
  const spots: [number, number, boolean][] = []
  const [l, rr] = feet(r)
  spots.push([l, 340, true], [l + 20, 230, false], [rr, 340, false], [rr - 20, 230, true], [(l + rr) / 2, 260, hash(3) > 0.5])
  for (const [x, w, flip] of spots) {
    const h = w * 76 / 512 * 0.9
    drawMat(ctx, dark, x, y, w, h, 0, 0.5, 1, 0, flip)
    if (seam > 0) drawMat(ctx, cold, x, y, w, h, 0, 0.5, seam, 0, flip, 'lighter')
  }
}

/** 装置の手前 (床が割れた瞬間の閃き・土ぼこり・灰・かけら・目の灯り)。二人と駒より後に呼ぶ。 */
export function drawDropFront(ctx: CanvasRenderingContext2D, e: number, r: Rect, eyes: [number, number][]): void {
  if (e < T.appear || e >= T.end + 400) return
  const s = e - T.appear
  const [l, rr] = feet(r), cx = (r.x0 + r.x1) / 2, y = r.bottom + 4
  // 床を打った閃き: 冷たい白が一瞬だけ (短く・弱く)。
  if (s < 140) {
    ctx.save(); ctx.globalCompositeOperation = 'lighter'
    ctx.fillStyle = `rgba(200,220,255,${0.1 * (1 - s / 140)})`; ctx.fillRect(r.x0 - 3000, -3000, 7000, 7000)
    ctx.restore()
  }
  // 足もとの土ぼこり (着地で左右へ)。
  const ash = '146,148,158', soot = '62,63,72'
  for (const [x, big] of [[l, 1], [rr, 1], [cx, 0.8]] as const) {
    const st = Math.min(3, Math.floor(s / 110))
    const img = dustSprite('land', st)
    const a = s < 700 ? 0.85 : 0.85 * (1 - clamp01((s - 700) / 900))
    if (img) drawMat(ctx, tinted(img, ash), x, y, 420 * big * (0.75 + 0.25 * clamp01(s / 300)), 300 * big, 0.5, 1, a)
    const kick = dustSprite('kick', Math.min(3, Math.floor(s / 90)))
    if (kick && s < 600) drawMat(ctx, tinted(kick, soot), x, y, 200 * big, 200 * big, 0.5, 1, 0.7 * (1 - s / 600))
  }
  // 灰の雲: 左右へ吹き出して床をはい、ゆっくり上がりながらほどける。
  for (let i = 0; i < 10; i++) {
    const side = i % 2 ? 1 : -1, k = hash(i + 7)
    const from = side > 0 ? rr : l
    const speed = 520 + 380 * k, ts = s / 1000
    const travel = speed * (1 - Math.exp(-ts * 2.6)) / 2.6
    const x = from + side * (40 + travel + i * 14), yy = y - 6 - ts * (20 + 30 * k)
    const step = Math.min(2, Math.floor(s / 600))
    const img = mistSprite(i, step)
    const a = 0.62 * clamp01(s / 120) * (1 - clamp01((s - 500) / 1400))
    if (img) drawMat(ctx, tinted(img, i % 3 ? ash : soot), x, yy, 170 + 90 * k + ts * 80, 220 + 60 * k + ts * 60, 0.5, 1, a, 0, side < 0)
  }
  // 割れた床のかけら (はね上がって落ちる)。
  for (let i = 0; i < 12; i++) {
    const k = hash(i + 31), side = i % 2 ? 1 : -1, from = (i % 4 < 2 ? (side > 0 ? rr : l) : cx)
    const ts = s / 1000
    const vx = side * (160 + 320 * k), vy = -(380 + 360 * hash(i + 41))
    const x = from + vx * ts, yy = y - 10 + vy * ts + 1500 * ts * ts
    if (yy > y + 6 || s > 1400) continue
    const img = crackBitSprite(i)
    if (img) drawMat(ctx, tinted(img, '74,74,82'), x, yy, 26 + 20 * k, 26 + 20 * k, 0.5, 0.5, 0.95, ts * (6 + 8 * k) * side)
  }
  // 吹き荒れる灰の小片 (黒と灰)。外へ速く飛んで、勢いを失って舞い落ちる。
  for (let i = 0; i < 28; i++) {
    const k = hash(i + 61), side = i % 2 ? 1 : -1, ts = s / 1000
    const v0 = 520 + 700 * k
    const travel = v0 * (1 - Math.exp(-ts * 3.2)) / 3.2
    const x = cx + side * ((r.x1 - r.x0) * 0.2 * hash(i + 71) + travel)
    const yy = y - 20 - 160 * hash(i + 81) * (1 - Math.exp(-ts * 4)) + 40 * ts + Math.sin(ts * 5 + i) * 8
    const a = clamp01(s / 80) * (1 - clamp01((s - 900) / 1100))
    const img = dustSprite('bit', i)
    if (img) drawMat(ctx, tinted(img, i % 3 ? '38,38,44' : '176,178,188'), x, yy, 16 + 16 * k, 16 + 16 * k, 0.5, 0.5, a, ts * 4 * side)
  }
  // 装置の目が冷たく灯る (床を割った直後・邪悪さ)。
  if (s >= 120 && s < 1500) {
    const v = s < 300 ? (s - 120) / 180 : 1 - clamp01((s - 300) / 1200)
    ctx.save(); ctx.globalCompositeOperation = 'lighter'
    for (const [ex, ey] of eyes) glow(ctx, ex, ey, 46 + 20 * v, 0.75 * v)
    ctx.restore()
  }
}
